import { GenericStoreType, IEvents } from './event-manager';
import { Store } from '../store';
import {
  taggingNCNextEventValue,
  taggingNCBackEventValue,
  tngaTaggingObjName,
  TngaWorkflowObject,
  TngaEventObject,
  TngaProductObject,
  mapT1ProductsToTngaProducts,
  taggingMappingCloseEventUC,
  ITngaProductInfo,
  taggingNCSpeedboatBackEventValue,
  taggingNCSpeedboatNextEventValue,
} from './config/common-config';
import { IInsurance, ITaggingEventNodes } from 'common-services/event-config';
import {
  tngaProductCategory,
  tngaProductSubcategory,
  ucInsuranceCalculator,
  scInsuranceCalculator,
} from './config/tnga-config';
import _ from 'lodash';

interface IDataLayerObjectProps {
  event: string;
}
declare global {
  interface Window {
    dataLayer?: Array<IDataLayerObjectProps>;
  }
}

export class TngaEventManager {
  public appSelector$: HTMLElement | null = null;

  public store: GenericStoreType = null;

  public taggingEvents: Record<string, Record<string, ITaggingEventNodes>> = {};

  constructor(
    appSelector: string,
    store: GenericStoreType,
    taggingEvents: Record<string, Record<string, ITaggingEventNodes>>
  ) {
    this.appSelector$ = appSelector !== '' ? document?.querySelector(appSelector) || null : null;
    this.taggingEvents = taggingEvents;
    this.store = store;
  }

  private handleWidgetLoaded(
    obj: IEvents,
    dataLayer: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    eventObj: TngaEventObject,
    workflowObj: TngaWorkflowObject,
    productsArray: Array<TngaProductObject>
  ): void {
    eventObj.label = (obj.data?.insuranceName as string) || eventObj.label;
    workflowObj.step = (obj.data?.insuranceName as string) || workflowObj.step;
    dataLayer.event = eventObj;
    dataLayer.workflow = workflowObj;
    this.addInsuranceToDataLayer(dataLayer, obj, productsArray);
  }

  private handleWidgetClosed(
    dataLayer: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    eventObj: TngaEventObject,
    workflowObj: TngaWorkflowObject
  ): void {
    const storeValue = this.store?.getTaggingData().value as string;
    const value =
      eventObj.componentName === ucInsuranceCalculator || eventObj.componentName === scInsuranceCalculator
        ? taggingMappingCloseEventUC[storeValue]
        : storeValue;
    workflowObj.step = value || null;
    eventObj.label = value || null;
    dataLayer.event = eventObj;
    dataLayer.workflow = workflowObj;
  }

  private resetDataLayerNodes(
    dataLayer: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    obj: IEvents
  ): void {
    dataLayer.event = {};
    if (
      dataLayer.hasOwnProperty('insurance') &&
      obj?.name !== 'optionProductAdded' &&
      obj?.name !== 'deductibleChosen'
    ) {
      delete dataLayer.insurance;
    }
    dataLayer.product = this.clearProductsWithInsuranceParam(dataLayer?.product);
  }

  private prepareWorkflowSource(workflowObj: TngaWorkflowObject, eventObj: TngaEventObject): void {
    if ((this.store as Store)?.getMarketConfig()?.parameters?.Origin === 'apheleia-carconfig') {
      workflowObj.source = 'apheleia-build';
    } else if ((this.store as Store)?.state?.themeModel === 'bz') {
      workflowObj.source = 'bz';
    } else if ((this.store as Store)?.state?.themeModel === 'speedboat') {
      workflowObj.source = 'speedboat-self-service';
      workflowObj.variant = workflowObj.variant || 'speedboat';
      eventObj.componentName = `${eventObj.componentName}-speedboat`;
    }
  }

  publishTaggingEvents(events: Array<IEvents>, appSelector = this.appSelector$): boolean {
    for (const obj of events) {
      if (!obj.tagging) continue;

      const dataLayer = window[tngaTaggingObjName];
      if (typeof dataLayer === 'undefined' || typeof dataLayer !== 'object') continue;

      const { event: eventConfig, workflow: workflowConfig } = this.taggingEvents.tnga[obj.name];
      const taggingEventName = eventConfig.name;
      const step = (this.store?.getTaggingData().step as number) * 1 || 0;
      let value = '';
      let isSpeedboat = false;

      // Reset dataLayer nodes
      this.resetDataLayerNodes(dataLayer, obj);

      // Prepare event and workflow objects
      const eventObj: TngaEventObject = JSON.parse(JSON.stringify(eventConfig));
      const workflowObj: TngaWorkflowObject = workflowConfig ? JSON.parse(JSON.stringify(workflowConfig)) : {};
      this.prepareWorkflowSource(workflowObj, eventObj);

      if ((this.store as Store)?.state?.themeModel === 'speedboat') {
        isSpeedboat = true;
      }

      const productsArray: Array<TngaProductObject> = dataLayer?.product;
      const productName = obj.data?.productName || '';
      const deductibleName = obj.data?.deductibleName || '';
      // prepare dataLayer nodes
      switch (obj.name) {
        case 'widgetLoaded':
          this.handleWidgetLoaded(obj, dataLayer, eventObj, workflowObj, productsArray);
          break;
        case 'widgetClosed':
          this.handleWidgetClosed(dataLayer, eventObj, workflowObj);
          break;
        case 'offersLoaded':
        case 'offersLoadedOfferShort':
        case 'offersLoadedOfferLong':
        case 'offerChosen':
        case 'deductiblesLoaded':
        case 'productsLoaded':
          dataLayer.event = eventObj;
          dataLayer.workflow = workflowObj;
          this.addInsuranceToDataLayer(dataLayer, obj, productsArray);
          break;
        case 'nextFail':
        case 'nextSuccess':
          value = isSpeedboat ? taggingNCSpeedboatNextEventValue[step] : taggingNCNextEventValue[step];
          eventObj.label = value || null;
          dataLayer.event = eventObj;
          break;
        case 'back':
          value = isSpeedboat ? taggingNCSpeedboatBackEventValue[step] : taggingNCBackEventValue[step];
          eventObj.label = value || null;

          dataLayer.event = eventObj;
          break;
        case 'offerReadMore':
          dataLayer.event = eventObj;
          dataLayer.event.label = step === 7 ? productName : `view-product-info-${productName}`;
          break;
        case 'optionProductAdded':
          dataLayer.event = {
            ...eventObj,
            label: `add-option-${productName}`,
          };
          dataLayer.workflow = {
            ...workflowObj,
            step: `add-option-${productName}`,
          };

          if (obj?.data && this.insuranceDataExist(obj?.data?.insurance)) {
            const insuranceObj: IInsurance = obj.data.insurance as IInsurance;

            dataLayer.insurance = insuranceObj;
          }
          break;
        case 'deductibleChosen':
          dataLayer.event = {
            ...eventObj,
            label: `select-deductible-${deductibleName}`,
          };
          dataLayer.workflow = {
            ...workflowObj,
            step: `select-deductible-${deductibleName}`,
          };
          if (obj?.data && this.insuranceDataExist(obj?.data?.insurance)) {
            const insuranceObj: IInsurance = obj.data.insurance as IInsurance;

            dataLayer.insurance = insuranceObj;
          }
          break;
        case 'sendEmail':
          dataLayer.event = {
            ...eventObj,
            label: `send-email-${productName}`,
          };
          break;
        case 'secondFormPageLoaded':
          dataLayer.event = eventObj;
          dataLayer.workflow = workflowObj;
          break;
        default:
          dataLayer.event = eventObj;
          if (!_.isEmpty(workflowObj)) dataLayer.workflow = workflowObj;
      }
      if (taggingEventName && appSelector !== null) {
        if (!(appSelector instanceof EventTarget)) {
          console.error('appSelector is not a valid EventTarget.');
          return true;
        }

        const event = new CustomEvent(taggingEventName, { bubbles: true });
        if (window?.dataLayer) window?.dataLayer.push({ event: taggingEventName });
        return appSelector.dispatchEvent(event);
      }
    }
    return true;
  }

  private insuranceDataExist = (insuranceData: string | Record<string, boolean> | IInsurance | undefined): boolean =>
    typeof insuranceData !== 'undefined' && Object.keys(insuranceData).length > 0;

  private clearProductsWithInsuranceParam = (
    currentProducts: Array<TngaProductObject> | undefined
  ): Array<TngaProductObject> =>
    currentProducts?.filter(
      (product) => product.category !== tngaProductCategory && product.subCategory !== tngaProductSubcategory
    ) || [];

  createProductObject = (
    currentProducts: Array<TngaProductObject>,
    insuranceObj: IInsurance,
    isCategory: boolean
  ): Array<TngaProductObject> => {
    const tngaProductObjWithoutInsuranceObj = currentProducts.filter(
      (product) => product.category !== tngaProductCategory && product.subCategory !== tngaProductSubcategory
    );

    const productsFromInsurance: TngaProductObject[] = isCategory
      ? mapT1ProductsToTngaProducts(insuranceObj?.products).map((product: ITngaProductInfo) => ({
          category: tngaProductCategory,
          subCategory: tngaProductSubcategory,
          productInfo: product,
        }))
      : mapT1ProductsToTngaProducts(insuranceObj?.products).map((product: ITngaProductInfo) => ({
          productInfo: product,
        }));

    return [...tngaProductObjWithoutInsuranceObj, ...productsFromInsurance];
  };

  private addInsuranceToDataLayer(
    dataLayer: Record<string, IInsurance | Array<TngaProductObject>>,
    obj: IEvents,
    productsArray: Array<TngaProductObject>
  ): void {
    if (obj?.data && this.insuranceDataExist(obj?.data?.insurance)) {
      const insuranceObj: IInsurance = obj.data.insurance as IInsurance;

      dataLayer.insurance = insuranceObj;
      if (obj.name === 'offerChosen' || obj.name === 'widgetLoaded') {
        dataLayer.product = this.createProductObject(productsArray, insuranceObj, true);
      }
    }
  }
}
